import { useState } from 'react';
import {
  DataGrid,
  GridCallbackDetails,
  GridColumns,
  GridRowsProp,
  GridSortModel
} from '@mui/x-data-grid';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import { PAGE_SIZES } from './utils';
import { getV2GridStyles } from '@/views/Pages/Retail3/TicketList/GridStyles';

type RestoreDataGridProps = {
  rowCount: number;
  columnConfig: GridColumns;
  rows: GridRowsProp;
  rowHeight?: number;
  loading: boolean;
  page: number;
  pageSize: number;
  pageSizes?: number[];
  sortModel: GridSortModel;
  hideFooter?: boolean;
  onPageChangeSize: (pageSize: number, details: GridCallbackDetails) => void;
  onPageChange: (page: number, direction: number) => void;
  onSortModelChange: (
    model: GridSortModel,
    details: GridCallbackDetails
  ) => void;
  onRowClick?: (params: any, event: any) => void;
  useV2Styles?: boolean;
  emptyListMessage?: string;
};

type DataGridComponents = {
  ColumnSortedAscendingIcon: typeof ArrowDropDownIcon;
  ColumnSortedDescendingIcon: typeof ArrowDropUpIcon;
  NoRowsOverlay?: React.FC;
  NoResultsOverlay?: React.FC;
};

const emptyListMsgStyles = {
  display: 'flex',
  justifyContent: 'center',
  fontStyle: 'italic',
  alignItems: 'center',
  height: 20,
  padding: 16,
  fontSize: 14
};

export const RestoreDataGrid = ({
  rowCount,
  columnConfig,
  rows,
  rowHeight = 44,
  loading,
  page,
  pageSize,
  pageSizes = PAGE_SIZES,
  sortModel,
  hideFooter,
  useV2Styles = false,
  emptyListMessage,
  onPageChangeSize,
  onPageChange,
  onSortModelChange,
  onRowClick
}: RestoreDataGridProps) => {
  const [sortModelState, setSortModelState] = useState({});
  const [lastPage, setLastPage] = useState(0);

  const components: DataGridComponents = {
    ColumnSortedAscendingIcon: ArrowDropDownIcon,
    ColumnSortedDescendingIcon: ArrowDropUpIcon
  };

  if (emptyListMessage) {
    components.NoRowsOverlay = () => (
      <div style={emptyListMsgStyles}>{emptyListMessage}</div>
    );
    components.NoResultsOverlay = () => (
      <div style={emptyListMsgStyles}>{emptyListMessage}</div>
    );
  }

  return (
    <DataGrid
      autoHeight
      scrollbarSize={17}
      rowHeight={rowHeight}
      rowCount={rowCount}
      disableVirtualization
      columns={columnConfig}
      rows={rows || []}
      loading={loading}
      pagination
      paginationMode="server"
      page={page}
      hideFooter={hideFooter}
      pageSize={pageSize}
      onPageSizeChange={(ps, d) => {
        onPageChangeSize(ps, d);
        onPageChange(0, 1);
      }}
      rowsPerPageOptions={pageSizes}
      onPageChange={p => {
        setLastPage(p);
        if (!loading) {
          onPageChange(p, p > lastPage ? 1 : -1);
        }
      }}
      sortingMode="server"
      sortingOrder={['desc', 'asc']}
      sortModel={sortModel}
      onRowClick={onRowClick}
      onSortModelChange={(m, d) => {
        // currently for a bug where onSortModelChange is called when sortModel is not changed
        if (loading) {
          return;
        } else if (m.length) {
          onSortModelChange(m, d);
        } else if (
          JSON.stringify(sortModel) !== JSON.stringify(sortModelState)
        ) {
          onSortModelChange(sortModel, d);
          setSortModelState(sortModel);
        }
      }}
      isRowSelectable={() => false}
      components={components}
      sx={useV2Styles ? getV2GridStyles(rows.length === 0) : {}}
    />
  );
};
